import React, { useEffect, useState } from 'react';
import { ShieldCheckIcon, DocumentTextIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { gdprService } from '../../services/gdprService';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

interface KYCDocument {
  id: string;
  userId: string;
  docType: string;
  status: string;
  createdAt: string;
  user: { id: string; email: string; username: string };
}

interface TaxForm {
  id: string;
  userId: string;
  formType: string;
  taxYear: number;
  status: string;
  createdAt: string;
  legalName: string;
}

interface GDPRRequest {
  id: string;
  userId: string;
  type: string;
  status: string;
  requestedAt: string;
  scheduledDeletionAt?: string;
}

const ComplianceReviewPage: React.FC = () => {
  const [kycDocs, setKycDocs] = useState<KYCDocument[]>([]);
  const [taxForms, setTaxForms] = useState<TaxForm[]>([]);
  const [gdprRequests, setGdprRequests] = useState<GDPRRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'kyc' | 'tax' | 'gdpr'>('kyc');

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');
      
      // Load KYC documents
      const kycRes = await axios.get(`${API_BASE_URL}/api/kyc/admin?status=PENDING`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKycDocs(kycRes.data.documents || []);

      // Load Tax forms
      const taxRes = await axios.get(`${API_BASE_URL}/api/tax/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTaxForms(taxRes.data || []);

      // Load GDPR requests
      const gdprRes = await gdprService.adminListRequests({ status: 'PENDING' });
      setGdprRequests(gdprRes as any);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load compliance items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleKYCApprove = async (id: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${API_BASE_URL}/api/kyc/${id}/status`,
        { status: 'APPROVED', note: 'Approved by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to approve KYC');
    }
  };

  const handleKYCReject = async (id: string) => {
    const note = prompt('Enter rejection reason:');
    if (!note) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${API_BASE_URL}/api/kyc/${id}/status`,
        { status: 'REJECTED', note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to reject KYC');
    }
  };

  const handleTaxApprove = async (id: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/api/tax/admin/review/${id}`,
        { status: 'APPROVED', notes: 'Approved by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to approve tax form');
    }
  };

  const handleTaxReject = async (id: string) => {
    const notes = prompt('Enter rejection reason:');
    if (!notes) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/api/tax/admin/review/${id}`,
        { status: 'REJECTED', notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to reject tax form');
    }
  };

  const handleGDPRApprove = async (id: string) => {
    try {
      await gdprService.adminApproveDeletion(id);
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Failed to approve deletion');
    }
  };

  const pendingCounts = {
    kyc: kycDocs.length,
    tax: taxForms.length,
    gdpr: gdprRequests.filter((r) => r.type === 'DELETE' && r.status === 'PENDING').length,
  };

  const totalPending = pendingCounts.kyc + pendingCounts.tax + pendingCounts.gdpr;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Compliance Review Queue</h1>
        <p className="text-gray-600 mt-1">Review and approve pending KYC documents, tax forms, and GDPR requests</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{totalPending}</p>
            </div>
            <UserGroupIcon className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">KYC Documents</p>
              <p className="text-2xl font-semibold text-blue-600">{pendingCounts.kyc}</p>
            </div>
            <ShieldCheckIcon className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tax Forms</p>
              <p className="text-2xl font-semibold text-green-600">{pendingCounts.tax}</p>
            </div>
            <DocumentTextIcon className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">GDPR Deletions</p>
              <p className="text-2xl font-semibold text-red-600">{pendingCounts.gdpr}</p>
            </div>
            <DocumentTextIcon className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-4">
          {['kyc', 'tax', 'gdpr'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-4 py-2 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.toUpperCase()} ({tab === 'kyc' ? pendingCounts.kyc : tab === 'tax' ? pendingCounts.tax : pendingCounts.gdpr})
            </button>
          ))}
        </nav>
      </div>

      {loading && <p className="text-gray-500 text-center py-6">Loading...</p>}

      {/* KYC Tab */}
      {activeTab === 'kyc' && !loading && (
        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {kycDocs.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    <div className="font-medium text-gray-900">{doc.user.username}</div>
                    <div className="text-gray-500">{doc.user.email}</div>
                  </td>
                  <td className="px-4 py-2 text-sm">{doc.docType}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{new Date(doc.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-sm flex gap-2">
                    <button
                      onClick={() => handleKYCApprove(doc.id)}
                      className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleKYCReject(doc.id)}
                      className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
              {kycDocs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                    No pending KYC documents
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tax Tab */}
      {activeTab === 'tax' && !loading && (
        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Legal Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Form Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tax Year</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {taxForms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium">{form.legalName}</td>
                  <td className="px-4 py-2 text-sm">{form.formType}</td>
                  <td className="px-4 py-2 text-sm">{form.taxYear}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{new Date(form.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-sm flex gap-2">
                    <button
                      onClick={() => handleTaxApprove(form.id)}
                      className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleTaxReject(form.id)}
                      className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
              {taxForms.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    No pending tax forms
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* GDPR Tab */}
      {activeTab === 'gdpr' && !loading && (
        <div className="bg-white shadow rounded overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scheduled</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {gdprRequests
                .filter((r) => r.type === 'DELETE' && r.status === 'PENDING')
                .map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{req.userId.slice(0, 8)}…</td>
                    <td className="px-4 py-2 text-sm font-medium">{req.type}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{new Date(req.requestedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {req.scheduledDeletionAt ? new Date(req.scheduledDeletionAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-sm flex gap-2">
                      <button
                        onClick={() => handleGDPRApprove(req.id)}
                        className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      >
                        Approve Deletion
                      </button>
                    </td>
                  </tr>
                ))}
              {gdprRequests.filter((r) => r.type === 'DELETE' && r.status === 'PENDING').length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    No pending GDPR deletion requests
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComplianceReviewPage;
